import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { cn } from "@/lib/cn";

const control =
  "bg-surface text-body min-h-11 w-full rounded-[12px] px-3.5 outline-none ring-accent/40 transition-shadow placeholder:text-muted/70 focus:ring-2";

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: (id: string) => ReactNode;
  className?: string;
}

export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  return (
    <div className={cn("space-y-1.5", className)}>
      <label htmlFor={id} className="block px-1 text-footnote font-medium text-muted">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="px-1 text-footnote text-negative" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="px-1 text-footnote text-muted">{hint}</p>
      )}
    </div>
  );
}

const PICKER_TYPES = new Set(["date", "datetime-local", "month", "week", "time"]);

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, type, onPointerDown, onFocus, onClick, ...rest }, ref) {
    const handleScrollIntoView = (el: HTMLInputElement) => {
      el.scrollIntoView?.({ block: "center", behavior: "auto" });
    };

    return (
      <input
        ref={ref}
        type={type}
        className={cn(control, className)}
        {...rest}
        onFocus={(e) => {
          onFocus?.(e);
          if (type && PICKER_TYPES.has(type)) {
            handleScrollIntoView(e.currentTarget);
          }
        }}
        onClick={(e) => {
          onClick?.(e);
          if (type && PICKER_TYPES.has(type)) {
            handleScrollIntoView(e.currentTarget);
          }
        }}
        onPointerDown={(e) => {
          onPointerDown?.(e);
          // Same vaul pointer capture as Select: once the input captures the pointer, the
          // click is retargeted from the calendar icon to the input and the picker never opens.
          if (type && PICKER_TYPES.has(type)) {
            e.stopPropagation();
            handleScrollIntoView(e.currentTarget);
          }
        }}
      />
    );
  },
);

export function Select({
  className,
  children,
  onPointerDown,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(control, "appearance-none bg-[length:12px] pr-9", className)}
      {...rest}
      onPointerDown={(e) => {
        onPointerDown?.(e);
        // Inside a vaul Sheet, pointerdown starts a drag and calls setPointerCapture on the
        // <select>. The native picker swallows the pointerup, so the capture is never released
        // and every later click lands on the select. A select never needs to drag the sheet.
        e.stopPropagation();
      }}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%238e8e93' stroke-width='1.6' stroke-linecap='round'/%3E%3C/svg%3E\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 14px center",
      }}
    >
      {children}
    </select>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative h-[31px] w-[51px] shrink-0 rounded-full transition-colors disabled:opacity-50",
        checked ? "bg-positive" : "bg-[var(--separator)]",
      )}
    >
      <span
        className={cn(
          "absolute top-[2px] left-[2px] size-[27px] rounded-full bg-white shadow transition-transform",
          checked && "translate-x-5",
        )}
      />
    </button>
  );
}

/** Big, centered amount input used at the top of add/edit sheets. */
export const AmountInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function AmountInput({ className, value, style, ...rest }, ref) {
    // Size the input to its content so the "$" stays attached to the number.
    const chars = Math.max(4, String(value ?? "").length || 4);
    return (
      <div className="flex items-baseline justify-center gap-1 py-2">
        <span className="text-title text-muted">$</span>
        <input
          ref={ref}
          value={value}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0.00"
          aria-label="Monto"
          style={{ width: `${chars + 0.6}ch`, ...style }}
          className={cn(
            "max-w-full bg-transparent text-center tabular text-hero outline-none placeholder:text-muted/40",
            className,
          )}
          {...rest}
        />
      </div>
    );
  },
);
